import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useThemeColors } from '../../hooks/useThemeColors';
import { limitSeries } from '../../utils/chartSeries';
import type { ChartSeries } from '../../utils/chartSeries';
import { formatUtc } from '../../utils/dateWindow';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

interface TimeSeriesChartProps {
  series: ChartSeries[];
  title: string;
  /** The y axis title: what is counted ("Unique people", "Trips"). */
  yLabel: string;
  /** What the lines are called in the footer, plural: "across 3 {seriesNoun}". */
  seriesNoun: string;
  /** More lines than this are hard to read, so only the largest are drawn. Default 10. */
  maxSeries?: number;
}

/** x tick label in UTC. The time scale hands its tick callback the tick's timestamp. */
export const utcTickLabel = (value: number | string): string => formatUtc(Number(value), 'hourly');

/** Tooltip title in UTC for the first hovered point. */
export const utcTooltipTitle = (items: Array<{ parsed: { x: number | null } }>): string => {
  const x = items[0]?.parsed.x;
  return x == null ? '' : formatUtc(x, 'hourly');
};

/** A date range for the footer, in UTC. */
export const utcDateRange = (fromMs: number, toMs: number): string =>
  `${formatUtc(fromMs, 'daily')} - ${formatUtc(toMs, 'daily')} (UTC)`;

const FONT = "'Inter', sans-serif";

/** Lines over time, always shown in UTC. A missing figure is a gap in its line, never a zero. */
const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ series, title, yLabel, seriesNoun, maxSeries = 10 }) => {
  const themeColors = useThemeColors();
  const colors = themeColors.chart;
  const palette = themeColors.series;

  const { shown, total } = useMemo(() => limitSeries(series, maxSeries), [series, maxSeries]);

  // Every line shares one x axis: the sorted union of all their instants.
  const instants = useMemo(() => {
    const all = new Set<number>();
    for (const s of shown) for (const p of s.points) all.add(Date.parse(p.t));
    return [...all].sort((a, b) => a - b);
  }, [shown]);

  const chartData = useMemo(
    () => ({
      labels: instants,
      datasets: shown.map((s, i) => {
        const at = new Map(s.points.map((p) => [Date.parse(p.t), p.value] as const));
        const color = palette[i % palette.length];
        return {
          label: s.label,
          data: instants.map((ms) => at.get(ms) ?? null),
          borderColor: color,
          backgroundColor: color,
          tension: 0,
          spanGaps: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
        };
      }),
    }),
    [shown, instants, palette],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: { usePointStyle: true, padding: 20, color: colors.tick, font: { size: 12, family: FONT } },
        },
        title: {
          display: !!title,
          text: title,
          color: colors.tick,
          font: { size: 16, weight: 'bold' as const, family: FONT },
          padding: { bottom: 20 },
        },
        tooltip: {
          backgroundColor: colors.tooltipBg,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: utcTooltipTitle,
            label: (context: { dataset: { label?: string }; parsed: { y: number | null } }) =>
              context.parsed.y == null
                ? `${context.dataset.label}: no data`
                : `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`,
          },
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          time: { displayFormats: { hour: 'MMM d, HH:mm', day: 'MMM d' } },
          ticks: { maxTicksLimit: 8, color: colors.tick, font: { size: 11, family: FONT }, callback: utcTickLabel },
          title: { display: true, text: 'UTC', color: colors.tick },
          grid: { color: colors.grid },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yLabel, color: colors.tick },
          ticks: {
            color: colors.tick,
            font: { size: 11, family: FONT },
            callback: (value: number | string) => (typeof value === 'number' ? value.toLocaleString() : value),
          },
          grid: { color: colors.grid },
        },
      },
      interaction: { mode: 'index' as const, intersect: false },
    }),
    [colors.tick, colors.grid, colors.tooltipBg, title, yLabel],
  );

  if (shown.length === 0 || instants.length === 0) {
    return (
      <div className="bg-base-100 rounded-lg p-6 border border-base-300">
        <div className="text-center text-base-content/60">
          <div className="text-2xl mb-2">📈</div>
          <p className="text-sm">No time series data available</p>
        </div>
      </div>
    );
  }

  const points = shown.reduce((n, s) => n + s.points.filter((p) => p.value != null).length, 0);
  const limited = shown.length < total;

  return (
    <div className="bg-base-100 rounded-lg p-4 border border-base-300 shadow-sm">
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
      <div className="mt-3 pt-3 border-t border-base-300">
        <div className="flex justify-between items-center text-xs text-base-content/60">
          <span>
            {limited
              ? `Showing the ${shown.length} largest of ${total} ${seriesNoun}`
              : `${points} data points across ${total} ${seriesNoun}`}
          </span>
          <span>{utcDateRange(instants[0], instants[instants.length - 1])}</span>
        </div>
      </div>
    </div>
  );
};

export default TimeSeriesChart;
